import { motion, useInView, useSpring, AnimatePresence } from "framer-motion";
import { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { Navbar, Footer } from "@/components/site-chrome";
import { Button } from "@/components/ui/button";
import {
  AreaChart, Area, BarChart, Bar, Cell, LineChart, Line,
  ResponsiveContainer, XAxis, YAxis, Tooltip, RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";
import {
  ChartIcon, TrendingUpIcon, ArrowRightIcon, ReceiptIcon, WalletIcon,
  StoreIcon, StudentsIcon, ParentsIcon, SchoolsIcon, SparkleIcon,
  GrowthIcon, ClockIcon, TargetIcon, ShieldIcon, BookIcon, TransportIcon,
} from "@/components/ui/TriIcons";

const EASE = [0.4, 0, 0.2, 1] as const;
const WINE = "#7B1416";
const SAND = "#C4A882";
const CHARCOAL = "#1C1917";
const CREAM = "#F5EFE4";
const CREAM2 = "#EDE4D4";
const FONT_DISPLAY = "'Georgia', 'Times New Roman', 'Playfair Display', serif";
const FONT_SANS = "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', system-ui, sans-serif";
const GRAIN = `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

export function InsightsPage() {
  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: CREAM, fontFamily: FONT_SANS }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.035]" style={{ backgroundImage: GRAIN, backgroundSize: "200px 200px" }} />
      <div className="pointer-events-none absolute inset-0 opacity-50" style={{ backgroundImage: `radial-gradient(circle, ${CHARCOAL}0A 1px, transparent 1px)`, backgroundSize: "32px 32px" }} />
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${WINE}, ${SAND}, ${WINE})` }} />
      <div className="relative z-10">
        <Navbar />
        <InsightsHero />
        <RevenueDashboard />
        <CollectionsBreakdown />
        <PaymentVelocity />
        <AIPredictionsSection />
        <RealTimeMetrics />
        <CTASection />
        <Footer />
      </div>
    </div>
  );
}

function InsightsHero() {
  return (
    <section className="relative overflow-hidden pt-32 pb-24 sm:pt-40 sm:pb-32">
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 80% 50% at 50% 0%, ${WINE}10 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 20% 60%, ${SAND}12 0%, transparent 50%)` }} />
      <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
          <div className="mb-8 flex items-center justify-center gap-4">
            <div className="h-px w-12" style={{ background: WINE }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: WINE, fontFamily: FONT_SANS }}>TriSphere Insights</span>
            <div className="h-px w-12" style={{ background: WINE }} />
          </div>
          <h1 style={{ fontFamily: FONT_DISPLAY, color: CHARCOAL, lineHeight: 1.02, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5rem)", fontWeight: 700 }}>
            Financial intelligence.
          </h1>
          <h1 style={{ fontFamily: FONT_DISPLAY, color: WINE, lineHeight: 1.02, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5rem)", fontWeight: 700, fontStyle: "italic" }}>
            In real time.
          </h1>
          <p className="mx-auto mt-8 max-w-2xl text-lg leading-[1.7]" style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: `${CHARCOAL}80` }}>
            Watch collections flow, track revenue trends, predict outcomes, and measure
            the pulse of your institution — live. No spreadsheets. No quarterly reports.
            Just the truth, in real time.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

function RevenueDashboard() {
  const [range, setRange] = useState<"7d" | "30d" | "90d">("30d");
  const data = useMemo(() => {
    if (range === "7d") return rev7d;
    if (range === "30d") return rev30d;
    return rev90d;
  }, [range]);

  return (
    <section className="py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6 sm:px-8">
        <div className="mb-6 flex items-center gap-5">
          <div className="h-px w-10" style={{ background: WINE }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: WINE, fontFamily: FONT_SANS }}>Revenue Analytics</span>
        </div>
        <h2 style={{ fontFamily: FONT_DISPLAY, color: CHARCOAL, lineHeight: 0.95, letterSpacing: "-0.03em", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 700 }}>
          Total collections over time
        </h2>

        <div className="mt-8 flex justify-center">
          <div className="flex rounded-none p-1" style={{ border: `1px solid ${CHARCOAL}15`, background: CREAM }}>
            {(["7d", "30d", "90d"] as const).map(r => (
              <button key={r} onClick={() => setRange(r)} className="rounded-none px-5 py-2 text-xs font-bold uppercase tracking-wider transition-all" style={range === r ? { background: WINE, color: "#fff", fontFamily: FONT_SANS } : { color: `${CHARCOAL}60`, fontFamily: FONT_SANS }}>
                {r === "7d" ? "7 Days" : r === "30d" ? "30 Days" : "90 Days"}
              </button>
            ))}
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-10 overflow-hidden p-6 sm:p-8" style={{ border: `1.5px solid ${CHARCOAL}15`, background: CREAM, boxShadow: "0 8px 40px -12px rgba(123,20,22,0.08)" }}>
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <div className="text-[9px] uppercase tracking-[0.25em]" style={{ color: WINE, fontFamily: FONT_SANS }}>Total Collected</div>
              <div className="mt-1 text-4xl font-bold" style={{ fontFamily: FONT_DISPLAY, color: CHARCOAL }}>
                <Counter target={range === "7d" ? 87_000_000 : range === "30d" ? 342_000_000 : 1_120_000_000} prefix="UGX " />
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm font-bold" style={{ color: WINE, fontFamily: FONT_SANS }}>
              <TrendingUpIcon className="h-4 w-4" accent={WINE} /> +24.8%
            </div>
          </div>
          <div className="mt-6 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 10, right: 8, left: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={WINE} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={WINE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="label" stroke={`${CHARCOAL}40`} fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke={`${CHARCOAL}40`} fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1_000_000}M`} />
                <Tooltip contentStyle={{ background: CREAM, border: `1px solid ${CHARCOAL}15`, borderRadius: 0, fontFamily: FONT_SANS }} formatter={(v: any) => [`UGX ${(v / 1_000_000).toFixed(1)}M`, "Collected"]} />
                <Area type="monotone" dataKey="v" stroke={WINE} strokeWidth={3} fill="url(#revFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function CollectionsBreakdown() {
  const data = [
    { name: "School Fees", value: 52, fill: WINE },
    { name: "Transport", value: 18, fill: SAND },
    { name: "Meals", value: 14, fill: "#A42022" },
    { name: "Marketplace", value: 10, fill: "#8A6520" },
    { name: "Other", value: 6, fill: "#A68A5A" },
  ];
  return (
    <section className="py-24 sm:py-32" style={{ background: CREAM2 }}>
      <div className="mx-auto max-w-6xl px-6 sm:px-8">
        <div className="mb-6 flex items-center gap-5">
          <div className="h-px w-10" style={{ background: WINE }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: WINE, fontFamily: FONT_SANS }}>Breakdown</span>
        </div>
        <h2 style={{ fontFamily: FONT_DISPLAY, color: CHARCOAL, lineHeight: 0.95, letterSpacing: "-0.03em", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 700 }}>
          Where revenue comes from
        </h2>
        <div className="mt-14 grid items-center gap-12 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="mx-auto h-80 w-full max-w-md">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="30%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar dataKey="value" cornerRadius={8} background />
                <Tooltip contentStyle={{ background: CREAM, border: `1px solid ${CHARCOAL}15`, borderRadius: 0, fontFamily: FONT_SANS }} formatter={(v: any, n: any) => [`${v}%`, n]} />
              </RadialBarChart>
            </ResponsiveContainer>
          </motion.div>
          <div className="space-y-3">
            {data.map((d, i) => (
              <motion.div key={d.name} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, ease: EASE }} className="flex items-center justify-between p-4" style={{ border: `1px solid ${CHARCOAL}12`, background: CREAM }}>
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3" style={{ background: d.fill }} />
                  <span className="text-sm font-semibold" style={{ color: CHARCOAL, fontFamily: FONT_SANS }}>{d.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="hidden h-2 w-24 overflow-hidden sm:block" style={{ background: CREAM2 }}>
                    <motion.div className="h-full" style={{ background: d.fill }} initial={{ width: 0 }} whileInView={{ width: `${d.value}%` }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.1, duration: 0.8 }} />
                  </div>
                  <span className="text-sm font-bold" style={{ color: d.fill, fontFamily: FONT_DISPLAY }}>{d.value}%</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PaymentVelocity() {
  return (
    <section className="py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6 sm:px-8">
        <div className="mb-6 flex items-center gap-5">
          <div className="h-px w-10" style={{ background: WINE }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: WINE, fontFamily: FONT_SANS }}>Payment Velocity</span>
        </div>
        <h2 style={{ fontFamily: FONT_DISPLAY, color: CHARCOAL, lineHeight: 0.95, letterSpacing: "-0.03em", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 700 }}>
          Transactions per hour
        </h2>
        <p className="mt-4 max-w-2xl text-lg" style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: `${CHARCOAL}70` }}>Peak hours see over 2,000 transactions per hour.</p>
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-12 overflow-hidden p-6 sm:p-8" style={{ border: `1.5px solid ${CHARCOAL}15`, background: CREAM, boxShadow: "0 8px 40px -12px rgba(123,20,22,0.08)" }}>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={velocityData} margin={{ top: 10, right: 8, left: 8, bottom: 0 }}>
                <XAxis dataKey="hour" stroke={`${CHARCOAL}40`} fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke={`${CHARCOAL}40`} fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: CREAM, border: `1px solid ${CHARCOAL}15`, borderRadius: 0, fontFamily: FONT_SANS }} formatter={(v: any) => [`${v} txns`, "Count"]} />
                <Bar dataKey="v" radius={[0, 0, 0, 0]}>
                  {velocityData.map((d, i) => (
                    <Cell key={i} fill={d.peak ? WINE : SAND} fillOpacity={d.peak ? 1 : 0.5} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function AIPredictionsSection() {
  const predictions = [
    { label: "Revenue forecasting", accuracy: "94%", color: SAND, icon: GrowthIcon },
    { label: "Dropout risk", accuracy: "89%", color: WINE, icon: ShieldIcon },
    { label: "Fee collection", accuracy: "91%", color: SAND, icon: ReceiptIcon },
    { label: "Attendance", accuracy: "96%", color: WINE, icon: ClockIcon },
    { label: "Academic performance", accuracy: "87%", color: SAND, icon: BookIcon },
    { label: "Transport delays", accuracy: "93%", color: WINE, icon: TransportIcon },
  ];
  return (
    <section className="relative overflow-hidden py-24 sm:py-32" style={{ background: CHARCOAL }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: GRAIN, backgroundSize: "200px 200px" }} />
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 30% 20%, ${WINE}18 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 70% 80%, ${SAND}10 0%, transparent 50%)` }} />
      <div className="relative z-10 mx-auto max-w-5xl px-6 sm:px-8">
        <div className="mb-6 flex items-center gap-5">
          <div className="h-px w-10" style={{ background: "rgba(255,255,255,0.25)" }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: "rgba(255,255,255,0.55)", fontFamily: FONT_SANS }}>AI Predictions</span>
        </div>
        <h2 style={{ fontFamily: FONT_DISPLAY, color: "white", lineHeight: 0.95, letterSpacing: "-0.03em", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 700 }}>
          Predict the future. <em className="italic" style={{ color: SAND }}>Before it happens.</em>
        </h2>
        <p className="mt-6 max-w-2xl text-lg" style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: "rgba(255,255,255,0.55)" }}>
          TriSphere AI continuously learns from every interaction — predicting outcomes
          before they happen, and recommending actions before they're needed.
        </p>
        <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {predictions.map((p, i) => (
            <motion.div key={p.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.4 }} className="p-6" style={{ border: `1px solid ${SAND}20`, background: `${SAND}08` }}>
              <div className="flex items-center justify-between">
                <div className="grid h-11 w-11 place-items-center" style={{ border: `1px solid ${p.color}30`, background: `${p.color}10` }}>
                  <p.icon className="h-5 w-5" accent={p.color} style={{ color: p.color }} />
                </div>
                <span className="text-3xl font-bold tabular-nums" style={{ fontFamily: FONT_DISPLAY, color: p.color }}>{p.accuracy}</span>
              </div>
              <h3 className="mt-4 text-sm font-medium" style={{ color: "rgba(255,255,255,0.85)", fontFamily: FONT_SANS }}>{p.label}</h3>
              <div className="mt-3 h-1.5 overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                <motion.div className="h-full" style={{ background: p.color }} initial={{ width: 0 }} whileInView={{ width: p.accuracy }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.1, duration: 0.8 }} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RealTimeMetrics() {
  const metrics = [
    { label: "Avg transaction value", value: "UGX 18,400", icon: ReceiptIcon, color: SAND },
    { label: "Checkout time", value: "2.1 seconds", icon: ClockIcon, color: WINE },
    { label: "Collection rate", value: "94%", icon: TargetIcon, color: SAND },
    { label: "Monthly growth", value: "+24.8%", icon: GrowthIcon, color: WINE },
  ];
  return (
    <section className="py-24 sm:py-32" style={{ background: CREAM2 }}>
      <div className="mx-auto max-w-6xl px-6 sm:px-8">
        <div className="mb-6 flex items-center gap-5">
          <div className="h-px w-10" style={{ background: WINE }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: WINE, fontFamily: FONT_SANS }}>Key Metrics</span>
        </div>
        <h2 style={{ fontFamily: FONT_DISPLAY, color: CHARCOAL, lineHeight: 0.95, letterSpacing: "-0.03em", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 700 }}>
          The numbers that matter
        </h2>
        <div className="mt-14 grid gap-px sm:grid-cols-2 lg:grid-cols-4" style={{ border: `1.5px solid ${CHARCOAL}15` }}>
          {metrics.map((m, i) => (
            <motion.div key={m.label} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ delay: i * 0.08, duration: 0.5, ease: EASE }} whileHover={{ y: -6 }} className="p-7 text-center" style={{ background: i % 2 === 0 ? CREAM : CREAM2, borderRight: i < 3 ? `1px solid ${CHARCOAL}10` : "none" }}>
              <div className="mx-auto grid h-14 w-14 place-items-center" style={{ border: `1.5px solid ${m.color}30`, background: `${m.color}08` }}>
                <m.icon className="h-7 w-7" accent={m.color} style={{ color: m.color }} />
              </div>
              <div className="mt-4 text-2xl font-bold" style={{ fontFamily: FONT_DISPLAY, color: m.color }}>{m.value}</div>
              <div className="mt-1.5 text-[9px] uppercase tracking-[0.25em]" style={{ color: `${CHARCOAL}50`, fontFamily: FONT_SANS }}>{m.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative overflow-hidden py-24 sm:py-32" style={{ background: WINE }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: GRAIN, backgroundSize: "200px 200px" }} />
      <div className="absolute top-0 left-0 right-0 h-2" style={{ background: SAND }} />
      <div className="absolute bottom-0 left-0 right-0 h-2" style={{ background: SAND }} />
      <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: EASE }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, color: "white", lineHeight: 0.92, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5rem)", fontWeight: 700 }}>
            Want these insights
          </h2>
          <p style={{ fontFamily: FONT_DISPLAY, color: SAND, lineHeight: 0.92, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5rem)", fontWeight: 700, fontStyle: "italic" }}>
            for your institution?
          </p>
          <p className="mt-8 text-lg" style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: "rgba(255,255,255,0.65)" }}>Get started today and see your numbers come alive.</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="h-12 rounded-none px-8 text-[13px] font-bold uppercase tracking-[0.15em] hover:opacity-90" style={{ background: SAND, color: CHARCOAL, border: "none", fontFamily: FONT_SANS }}>
              <Link to="/auth">Get Started <ArrowRightIcon className="ml-2 h-4 w-4" accent={CHARCOAL} style={{ color: CHARCOAL }} /></Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="h-12 rounded-none px-7 text-[13px] font-bold uppercase tracking-[0.15em] text-white hover:bg-white/10" style={{ border: `1.5px solid ${SAND}50`, fontFamily: FONT_SANS }}>
              <Link to="/ecosystem">Explore Ecosystem <SparkleIcon className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Counter({ target, prefix = "" }: { target: number; prefix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [display, setDisplay] = useState(0);
  const spring = useSpring(0, { stiffness: 60, damping: 20 });
  useEffect(() => { if (inView) spring.set(target); }, [inView, target, spring]);
  useEffect(() => { return spring.on("change", (v) => setDisplay(v)); }, [spring]);
  const formatted = target >= 1_000_000 ? `${(display / 1_000_000).toFixed(1)}M` : Math.round(display).toLocaleString();
  return <span ref={ref}>{prefix}{formatted}</span>;
}

const rev7d = [
  { label: "Mon", v: 9_500_000 }, { label: "Tue", v: 12_000_000 }, { label: "Wed", v: 10_500_000 },
  { label: "Thu", v: 14_500_000 }, { label: "Fri", v: 18_200_000 }, { label: "Sat", v: 13_800_000 }, { label: "Sun", v: 8_500_000 },
];
const rev30d = Array.from({ length: 30 }, (_, i) => ({ label: `${i + 1}`, v: 8_000_000 + Math.round(Math.sin(i * 0.3) * 3_000_000) + i * 200_000 }));
const rev90d = Array.from({ length: 12 }, (_, i) => ({ label: `W${i + 1}`, v: 25_000_000 + Math.round(Math.sin(i * 0.4) * 8_000_000) + i * 6_000_000 }));
const velocityData = [
  { hour: "6am", v: 180 }, { hour: "7am", v: 520 }, { hour: "8am", v: 1850, peak: true },
  { hour: "9am", v: 1240 }, { hour: "10am", v: 680 }, { hour: "11am", v: 540 },
  { hour: "12pm", v: 1620, peak: true }, { hour: "1pm", v: 1340 }, { hour: "2pm", v: 720 },
  { hour: "3pm", v: 580 }, { hour: "4pm", v: 920 }, { hour: "5pm", v: 1480, peak: true },
];

export default InsightsPage;
